import React from 'react';
import { Autocomplete, Box, TextField } from '@mui/material';

import {
  COLLAB_CLUB_GENRES,
  COLLAB_CLUB_SKILLS,
  WORK_PREFERENCES,
  WorkPreference,
} from 'constants/collab-club';
import { COUNTRIES } from 'constants/countries';

interface OnboardingFieldsProps {
  location: string | null;
  genres: string[];
  skills: string[];
  workPreference: WorkPreference | '';
  onLocationChange: (value: string | null) => void;
  onGenresChange: (value: string[]) => void;
  onSkillsChange: (value: string[]) => void;
  onWorkPreferenceChange: (value: WorkPreference) => void;
  // Optional action rendered inline with the preference toggle, pushed to the right.
  preferenceAction?: React.ReactNode;
}

// The editable onboarding answers for a CollabClub artist (location / genres / skills / work
// preference), each on its own full-width line. Controlled — used by the per-artist profile
// card in the admin Log Request / Log Match modals.
const OnboardingFields: React.FC<OnboardingFieldsProps> = ({
  location,
  genres,
  skills,
  workPreference,
  onLocationChange,
  onGenresChange,
  onSkillsChange,
  onWorkPreferenceChange,
  preferenceAction,
}) => (
  <Box display="flex" flexDirection="column" gap={2.5}>
    {/* Location — full width */}
    <Box>
      {/* <FieldLabel>Location</FieldLabel> */}
      <Autocomplete
        freeSolo
        autoSelect
        options={COUNTRIES}
        value={location}
        onChange={(_, value) => onLocationChange(value)}
        renderInput={(params) => (
          <TextField {...params} size="small" placeholder="Select or type a country…" />
        )}
      />
    </Box>

    {/* Genres — full width */}
    <Box>
      {/* <FieldLabel>Genres</FieldLabel> */}
      <Autocomplete
        multiple
        freeSolo
        options={COLLAB_CLUB_GENRES}
        value={genres}
        onChange={(_, value) => onGenresChange(value)}
        renderInput={(params) => (
          <TextField {...params} size="small" placeholder="Select or type genres…" />
        )}
      />
    </Box>

    {/* Skills — full width */}
    <Box>
      {/* <FieldLabel>Skills</FieldLabel> */}
      <Autocomplete
        multiple
        freeSolo
        options={COLLAB_CLUB_SKILLS}
        value={skills}
        onChange={(_, value) => onSkillsChange(value)}
        renderInput={(params) => (
          <TextField {...params} size="small" placeholder="Select or type skills…" />
        )}
      />
    </Box>

    {/* Work preference */}
    <Box>
      {/* <FieldLabel>Preference</FieldLabel> */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1.5}
        flexWrap="wrap"
      >
        <Box
          display="inline-flex"
          border="1px solid #e2e8f0"
          borderRadius="6px"
          overflow="hidden"
          bgcolor="#fff"
          flexWrap="wrap"
        >
          {(Object.keys(WORK_PREFERENCES) as WorkPreference[]).map((value, idx) => (
            <Box
              key={value}
              component="button"
              type="button"
              onClick={() => onWorkPreferenceChange(value)}
              padding="7px 16px"
              fontWeight={500}
              sx={{
                cursor: 'pointer',
                border: 'none',
                borderLeft: idx > 0 ? '1px solid #e2e8f0' : 'none',
                fontFamily: 'inherit',
                color: workPreference === value ? '#0f172a' : '#64748b',
                backgroundColor: workPreference === value ? '#f1f2f4' : 'transparent',
              }}
            >
              {WORK_PREFERENCES[value]}
            </Box>
          ))}
        </Box>
        {preferenceAction}
      </Box>
    </Box>
  </Box>
);

export default OnboardingFields;
